<!--
// app/components/pages/mercato/simulator/MercatoSquadSummary.vue
// @component MercatoSquadSummary - Displays final squad with download functionality
-->
<script setup lang="ts">
import { inject, type Ref } from "vue";
import type { MercatoTeam } from "~~/src/mercato/domain/models/MercatoTeam";
import type { MercatoPlayer } from "~~/src/mercato/domain/models/MercatoPlayer";
import { setAliasUrl } from "~~/src/Shared/utils";
import DownloadIcon from "~/components/Shared/icons/DownloadIcon.vue";

type Props = {
  team: MercatoTeam;
  squad: MercatoPlayer[];
};

const props = defineProps<Props>();

const emit = defineEmits<{
  download: [];
}>();

const { $t, $tn } = useI18n();
const { playerPlaceholderPath } = useImages();

const isDownloading = ref(false);
const domToImageReady = inject<Ref<boolean>>("domToImageReady", ref(false));
const domToImageError = inject<Ref<boolean>>("domToImageError", ref(false));

// Calculate total market value
const totalMarketValue = computed(() => {
  return props.squad.reduce((sum, player) => {
    if (player.marketValue === "free") return sum;
    return sum + Number(player.marketValue);
  }, 0);
});

// Group players by position
const playersByPosition = computed(() => {
  const positions = {
    goalkeeper: [] as MercatoPlayer[],
    defender: [] as MercatoPlayer[],
    midfielder: [] as MercatoPlayer[],
    forward: [] as MercatoPlayer[],
  };

  props.squad.forEach(player => {
    const position = player.position as keyof typeof positions;
    if (positions[position]) {
      positions[position].push(player);
    }
  });

  return positions;
});

const handleDownload = async () => {
  isDownloading.value = true;
  emit("download");

  // Use library directly without setTimeout
  if (typeof window !== "undefined" && window.domtoimage) {
    const captureElement = document.getElementById("mercatoSquadCapture");

    if (captureElement) {
      try {
        const dataUrl = await window.domtoimage.toPng(captureElement, {
          quality: 1,
          bgcolor: "#000000",
        });

        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${props.team.completeName}_Mercato_Squad.png`;
        link.click();
        link.remove();
        isDownloading.value = false;
      } catch (error) {
        console.error("Error generating screenshot:", error);
        isDownloading.value = false;
      }
    }
  }
};
</script>

<template>
  <div class="mercato-squad-summary">
    <!-- Download Button -->
    <div class="mercato-squad-summary__actions">
      <button
        type="button"
        class="btn-download"
        :disabled="isDownloading"
        @click="handleDownload"
        :aria-label="$t('mercatoSimulator.button.download')"
      >
        <DownloadIcon class="w-5 h-5" />
        <span>{{ isDownloading ? $t("general.downloading") : $t("mercatoSimulator.button.download") }}</span>
      </button>
    </div>

    <div :class="{ 'blur-2xl opacity-60': isDownloading }">
      <!-- Capture Section (will be downloaded as image) -->
      <div
        id="mercatoSquadCapture"
        class="mercato-squad-summary__capture"
        :class="{ 'w-[1200px] h-auto ': isDownloading }"
      >
        <!-- Header -->
        <div class="capture-header">
          <img
            :src="'/_ipx/w_80&h_80/' + setAliasUrl(team.logo.url)"
            :alt="team.completeName"
            width="80"
            height="80"
            sizes="80px"
            class="rounded-lg"
          />
          <div class="capture-header__info">
            <h2 class="text-3xl font-bold">{{ team.completeName }}</h2>
            <p class="text-gray-400 text-lg mt-1">
              {{ squad.length }} {{ $t("menuItem.players") }} •
              <span class="text-mercato font-semibold">{{ $tn(totalMarketValue) }} M</span>
            </p>
          </div>

          <div class="hidden md:block">
            <img
              src="/img/mercato/mercato-logo.svg"
              alt="Kings League Mercato 2026"
              width="250"
              height="auto"
            />
          </div>
        </div>

        <!-- Squad by Position -->
        <div class="capture-squad">
          <!-- Goalkeepers -->
          <div v-if="playersByPosition.goalkeeper.length > 0" class="position-group">
            <h3 class="position-group__title">{{ $t("goalkeeper") }}</h3>
            <div :class="['position-group__players', { '!grid-cols-3': isDownloading }]">
              <div v-for="player in playersByPosition.goalkeeper" :key="player.id" class="player-item">
                <img
                  :src="
                    player.imageUrl
                      ? '/_ipx/w_40&h_40/' + setAliasUrl(player.imageUrl)
                      : playerPlaceholderPath
                  "
                  :alt="player.name"
                  width="40"
                  height="40"
                  sizes="40px"
                  class="player-item__photo"
                />
                <div class="player-item__info">
                  <p class="player-item__name">{{ player.name }}</p>
                  <p class="player-item__value">
                    {{
                      player.marketValue === "free"
                        ? $t("mercato.free")
                        : `${$tn(Number(player.marketValue))} M`
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Defenders -->
          <div v-if="playersByPosition.defender.length > 0" class="position-group">
            <h3 class="position-group__title">{{ $t("defender") }}</h3>
            <div :class="['position-group__players', { '!grid-cols-3': isDownloading }]">
              <div v-for="player in playersByPosition.defender" :key="player.id" class="player-item">
                <img
                  :src="
                    player.imageUrl
                      ? '/_ipx/w_40&h_40/' + setAliasUrl(player.imageUrl)
                      : playerPlaceholderPath
                  "
                  :alt="player.name"
                  width="40"
                  height="40"
                  sizes="40px"
                  class="player-item__photo"
                />
                <div class="player-item__info">
                  <p class="player-item__name">{{ player.name }}</p>
                  <p class="player-item__value">
                    {{
                      player.marketValue === "free"
                        ? $t("mercato.free")
                        : `${$tn(Number(player.marketValue))} M`
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Midfielders -->
          <div v-if="playersByPosition.midfielder.length > 0" class="position-group">
            <h3 class="position-group__title">{{ $t("midfielder") }}</h3>
            <div :class="['position-group__players', { '!grid-cols-3': isDownloading }]">
              <div v-for="player in playersByPosition.midfielder" :key="player.id" class="player-item">
                <img
                  :src="
                    player.imageUrl
                      ? '/_ipx/w_40&h_40/' + setAliasUrl(player.imageUrl)
                      : playerPlaceholderPath
                  "
                  :alt="player.name"
                  width="40"
                  height="40"
                  sizes="40px"
                  class="player-item__photo"
                />
                <div class="player-item__info">
                  <p class="player-item__name">{{ player.name }}</p>
                  <p class="player-item__value">
                    {{
                      player.marketValue === "free"
                        ? $t("mercato.free")
                        : `${$tn(Number(player.marketValue))} M`
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Forwards -->
          <div v-if="playersByPosition.forward.length > 0" class="position-group">
            <h3 class="position-group__title">{{ $t("forward") }}</h3>
            <div :class="['position-group__players', { '!grid-cols-3': isDownloading }]">
              <div v-for="player in playersByPosition.forward" :key="player.id" class="player-item">
                <img
                  :src="
                    player.imageUrl
                      ? '/_ipx/w_40&h_40/' + setAliasUrl(player.imageUrl)
                      : playerPlaceholderPath
                  "
                  :alt="player.name"
                  width="40"
                  height="40"
                  sizes="40px"
                  class="player-item__photo"
                />
                <div class="player-item__info">
                  <p class="player-item__name">{{ player.name }}</p>
                  <p class="player-item__value">
                    {{
                      player.marketValue === "free"
                        ? $t("mercato.free")
                        : `${$tn(Number(player.marketValue))} M`
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mercato-squad-summary {
  @apply flex flex-col gap-4 overflow-hidden;
}

.mercato-squad-summary__actions {
  @apply flex justify-end;
}

.btn-download {
  @apply bg-mercato text-black px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:bg-opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed;
}

/* Capture Styles (optimized for image download) */
.mercato-squad-summary__capture {
  @apply bg-black border-2 border-mercato rounded-lg p-8 bg-[url('/img/mercato/bg-summary-simulator.png')] bg-cover;
}

.capture-header {
  @apply flex items-center gap-6 mb-8 pb-6 border-b border-gray-700;
}

.capture-header__info {
  @apply flex-1;
}

.capture-squad {
  @apply flex flex-col gap-6;
}

.position-group {
  @apply flex flex-col gap-3;
}

.position-group__title {
  @apply text-mercato font-bold text-lg uppercase tracking-wide;
}

.position-group__players {
  @apply grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3;
}

.player-item {
  @apply flex items-center gap-3 bg-gray-900 rounded-lg p-3;
}

.player-item__photo {
  @apply rounded-full object-cover object-top bg-gray-700 w-10 h-10;
}

.player-item__info {
  @apply flex-1 min-w-0;
}

.player-item__name {
  @apply font-semibold text-sm truncate;
}

.player-item__value {
  @apply text-xs text-mercato font-semibold;
}
</style>
